import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Ban,
  Building2,
  CalendarDays,
  Check,
  Download,
  FileText,
  Info,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { rotuloTipo } from "@/lib/tipoProfissional";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { formatarData } from "@/lib/utils";

// ============================================================
// R2 (08/09) — Ficha do colaborador
// ============================================================
// Componente separado da página de propósito: a mesma ficha serve o financeiro
// (dentro de /colaboradores, podendo editar) e o próprio profissional (em
// modo leitura, quando a frente do extrato quiser embutir aqui).
//
// O que esta tela NÃO faz: gravar cadastro fiscal no banco do escalas. Todo
// campo abaixo é do ERP da clínica e volta para lá pelo PUT .../fiscal. A
// única exceção é o SETOR, que é do RH e mora no escalas.

// 25/09/2026: estágio e freela — o ERP aceita as seis desde 16/09 e o backend
// do RH passou a aceitar também (colaboradores.ts, esquemaModalidade).
type Modalidade = "clt" | "pf_autonomo" | "pj" | "socio" | "estagio" | "freela";
type Setor = "clinica" | "agro" | "bt" | null;

const ROTULO_SETOR: Record<string, string> = {
  clinica: "Clínica",
  agro: "Agro",
  bt: "Banho & Tosa",
};

const ROTULO_MODALIDADE: Record<string, string> = {
  clt: "CLT",
  pf_autonomo: "PF autônomo",
  pj: "PJ",
  socio: "Sócio",
  estagio: "Estágio",
  freela: "Freela (eventual)",
};

type Campo = { campo: string; rotulo: string; tipo?: "texto" | "data"; dica?: string };

const GRUPOS: Array<{ titulo: string; nota?: string; campos: Campo[]; so?: Modalidade[] }> = [
  {
    titulo: "Identificação",
    campos: [
      { campo: "cpf", rotulo: "CPF" },
      { campo: "rg", rotulo: "RG" },
      { campo: "rg_orgao", rotulo: "Órgão emissor" },
      { campo: "data_nascimento", rotulo: "Nascimento", tipo: "data" },
      { campo: "nacionalidade", rotulo: "Nacionalidade" },
      { campo: "estado_civil", rotulo: "Estado civil" },
      { campo: "pis_nit", rotulo: "PIS/NIT" },
    ],
  },
  {
    titulo: "Contato",
    campos: [
      { campo: "email", rotulo: "E-mail" },
      { campo: "telefone", rotulo: "Telefone" },
      { campo: "whatsapp", rotulo: "WhatsApp" },
    ],
  },
  {
    titulo: "Endereço",
    nota: "Qualificação civil da parte no contrato.",
    campos: [
      { campo: "cep", rotulo: "CEP" },
      { campo: "logradouro", rotulo: "Logradouro" },
      { campo: "numero", rotulo: "Número" },
      { campo: "complemento", rotulo: "Complemento" },
      { campo: "bairro", rotulo: "Bairro" },
      { campo: "cidade", rotulo: "Cidade" },
      { campo: "estado", rotulo: "UF" },
    ],
  },
  {
    titulo: "Empresa do prestador",
    nota: "Só faz sentido em PJ ou sócio.",
    so: ["pj", "socio"],
    campos: [
      { campo: "cnpj", rotulo: "CNPJ" },
      { campo: "razao_social", rotulo: "Razão social" },
      { campo: "inscricao_municipal", rotulo: "Inscrição municipal" },
      { campo: "cnae", rotulo: "CNAE" },
      { campo: "regime_tributario", rotulo: "Regime tributário" },
      { campo: "pj_representante_nome", rotulo: "Representante legal" },
      { campo: "pj_representante_cpf", rotulo: "CPF do representante" },
    ],
  },
  {
    titulo: "Registro profissional",
    campos: [
      { campo: "crmv", rotulo: "CRMV" },
      { campo: "crmv_uf", rotulo: "UF do CRMV" },
      { campo: "crmv_pj", rotulo: "CRMV da PJ" },
      { campo: "rt_art", rotulo: "ART / responsabilidade técnica" },
    ],
  },
  {
    titulo: "Dados bancários",
    nota: "É por onde o pagamento sai.",
    campos: [
      { campo: "banco", rotulo: "Banco" },
      { campo: "agencia", rotulo: "Agência" },
      { campo: "conta", rotulo: "Conta" },
      { campo: "conta_tipo", rotulo: "Tipo de conta" },
      { campo: "chave_pix", rotulo: "Chave Pix" },
      { campo: "chave_pix_tipo", rotulo: "Tipo da chave" },
    ],
  },
  {
    titulo: "Vínculo",
    campos: [
      { campo: "data_admissao", rotulo: "Admissão", tipo: "data" },
      { campo: "data_demissao", rotulo: "Saída", tipo: "data" },
    ],
  },
];

const CAMPOS_SOCIETARIO: Array<{ campo: string; rotulo: string; tipo: "texto" | "data" | "reais" | "inteiro" }> = [
  { campo: "quotas_percentual", rotulo: "Quotas (%)", tipo: "texto" },
  { campo: "quotas_quantidade", rotulo: "Quantidade de quotas", tipo: "inteiro" },
  { campo: "aporte_valor", rotulo: "Aporte (R$)", tipo: "reais" },
  { campo: "aporte_forma", rotulo: "Forma do aporte", tipo: "texto" },
  { campo: "pro_labore_mensal", rotulo: "Pró-labore mensal (R$)", tipo: "reais" },
  { campo: "jucesc_numero", rotulo: "Nº JUCESC", tipo: "texto" },
  { campo: "jucesc_data", rotulo: "Data JUCESC", tipo: "data" },
  { campo: "acordo_socios_assinado_em", rotulo: "Acordo de sócios assinado em", tipo: "data" },
  { campo: "termo_adesao_assinado_em", rotulo: "Termo de adesão assinado em", tipo: "data" },
  { campo: "contador_responsavel", rotulo: "Contador responsável", tipo: "texto" },
];

function comoTexto(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

function soData(v: unknown): string {
  const s = comoTexto(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
}

function dataBr(v: string | null | undefined): string {
  const s = soData(v);
  return s ? formatarData(s) : "—";
}

/** Aniversário: só dia e mês, que é o que o RH usa no quadro. */
function aniversario(iso: string | null | undefined): string | null {
  const s = soData(iso);
  if (!s) return null;
  const [, m, d] = s.split("-");
  return `${d}/${m}`;
}

function baixarArquivo(base64: string, mime: string, nome: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function FichaColaborador({
  profissionalId,
  somenteLeitura = false,
}: {
  profissionalId: number;
  somenteLeitura?: boolean;
}) {
  const utils = trpc.useUtils();
  const [simulada, setSimulada] = useState<Modalidade | null>(null);
  const q = trpc.colaboradores.ficha.useQuery({ profissionalId, modalidade: simulada });

  const [edicao, setEdicao] = useState<Record<string, string>>({});
  const [edicaoSoc, setEdicaoSoc] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const arquivoRef = useRef<HTMLInputElement | null>(null);
  const [nomeDoc, setNomeDoc] = useState("");
  const [vencDoc, setVencDoc] = useState("");
  const [enviando, setEnviando] = useState(false);

  // Trocar de pessoa zera o rascunho — nada de carregar edição de um na ficha do outro.
  useEffect(() => {
    setEdicao({});
    setEdicaoSoc({});
    setAviso(null);
    setSimulada(null);
    setNomeDoc("");
    setVencDoc("");
  }, [profissionalId]);

  const salvarFiscal = trpc.colaboradores.salvarFiscal.useMutation({
    onSuccess: (r) => {
      setEdicao({});
      setEdicaoSoc({});
      setAviso({ tipo: "ok", texto: `${r.camposGravados.length} campo(s) gravados na ficha da clínica.` });
      utils.colaboradores.ficha.invalidate({ profissionalId });
      utils.colaboradores.listar.invalidate();
    },
    onError: (e) => setAviso({ tipo: "erro", texto: e.message }),
  });

  const salvarSetor = trpc.colaboradores.salvarSetor.useMutation({
    onSuccess: () => {
      utils.colaboradores.ficha.invalidate({ profissionalId });
      utils.colaboradores.listar.invalidate();
    },
    onError: (e) => setAviso({ tipo: "erro", texto: e.message }),
  });

  const enviarDoc = trpc.colaboradores.enviarDocumento.useMutation({
    onSuccess: () => {
      setNomeDoc("");
      setVencDoc("");
      if (arquivoRef.current) arquivoRef.current.value = "";
      setAviso({ tipo: "ok", texto: "Documento anexado à ficha da clínica." });
      utils.colaboradores.ficha.invalidate({ profissionalId });
      utils.colaboradores.listar.invalidate();
    },
    onError: (e) => setAviso({ tipo: "erro", texto: e.message }),
  });

  const removerDoc = trpc.colaboradores.removerDocumento.useMutation({
    onSuccess: () => {
      setAviso({ tipo: "ok", texto: "Documento removido." });
      utils.colaboradores.ficha.invalidate({ profissionalId });
      utils.colaboradores.listar.invalidate();
    },
    onError: (e) => setAviso({ tipo: "erro", texto: e.message }),
  });

  const prof = q.data?.profissional ?? null;
  const dados = q.data?.dados ?? null;
  const ficha = (dados?.ficha ?? {}) as Record<string, unknown>;
  const societario = (dados?.societario ?? {}) as Record<string, unknown>;
  const checklist = dados?.checklist ?? null;
  const podeEditar = Boolean(q.data?.podeEditar) && !somenteLeitura && Boolean(dados);
  // 25/09/2026 (ERP f75d6ad): o que falta no cadastro da EMPRESA contratante.
  // Fica separado das pendências da pessoa — não entra no "faltam N campos"
  // nem no badge — e só aparece para o RH (não na ficha que o colaborador vê).
  const pendenciasEmpresa = checklist?.pendenciasContratante ?? [];
  const empresaContratante = checklist?.contratante ?? null;

  const modalidadeAtual = (comoTexto(ficha.modalidade) || null) as Modalidade | null;
  const modalidadeEmUso = (edicao.modalidade as Modalidade | undefined) ?? simulada ?? modalidadeAtual;

  const valor = (campo: string): string =>
    edicao[campo] !== undefined ? edicao[campo]! : campoDaFicha(campo);

  function campoDaFicha(campo: string): string {
    const bruto = ficha[campo];
    const ehData = GRUPOS.some((g) => g.campos.some((c) => c.campo === campo && c.tipo === "data"));
    return ehData ? soData(bruto) : comoTexto(bruto);
  }

  function mudar(campo: string, v: string) {
    setEdicao((atual) => ({ ...atual, [campo]: v }));
  }

  function valorSoc(campo: string, tipo: string): string {
    if (edicaoSoc[campo] !== undefined) return edicaoSoc[campo]!;
    const bruto = societario[campo];
    if (bruto === null || bruto === undefined) return "";
    if (tipo === "reais") return (Number(bruto) / 100).toFixed(2);
    if (tipo === "data") return soData(bruto);
    return String(bruto);
  }

  const camposAlterados = useMemo(() => {
    const saida: Record<string, unknown> = {};
    for (const [campo, v] of Object.entries(edicao)) {
      const original = campo === "modalidade" ? comoTexto(ficha.modalidade) : campoDaFicha(campo);
      if (v === original) continue;
      saida[campo] = v.trim() === "" ? null : v.trim();
    }
    const soc: Record<string, unknown> = {};
    for (const [campo, v] of Object.entries(edicaoSoc)) {
      const def = CAMPOS_SOCIETARIO.find((c) => c.campo === campo);
      if (!def) continue;
      if (v.trim() === "") {
        soc[campo] = null;
        continue;
      }
      if (def.tipo === "reais") soc[campo] = Math.round(Number(v.replace(",", ".")) * 100);
      else if (def.tipo === "inteiro") soc[campo] = Math.round(Number(v));
      else soc[campo] = v.trim();
    }
    if (Object.keys(soc).length > 0) saida.societario = soc;
    return saida;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edicao, edicaoSoc, dados]);

  const temMudanca = Object.keys(camposAlterados).length > 0;

  function focarCampo(campo: string) {
    const el = document.getElementById(`campo-${campo}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      (el as HTMLInputElement).focus?.();
    }
  }

  async function anexar() {
    const arquivo = arquivoRef.current?.files?.[0];
    if (!arquivo) {
      setAviso({ tipo: "erro", texto: "Escolha um arquivo primeiro." });
      return;
    }
    const ext = arquivo.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      setAviso({ tipo: "erro", texto: "Só PDF, JPG ou PNG." });
      return;
    }
    if (arquivo.size > 10 * 1024 * 1024) {
      setAviso({ tipo: "erro", texto: `Arquivo de ${(arquivo.size / 1048576).toFixed(1)} MB — o limite é 10 MB.` });
      return;
    }
    setEnviando(true);
    try {
      const base64: string = await new Promise((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onload = () => resolve(String(leitor.result));
        leitor.onerror = () => reject(new Error("Não consegui ler o arquivo."));
        leitor.readAsDataURL(arquivo);
      });
      await enviarDoc.mutateAsync({
        profissionalId,
        nome: nomeDoc.trim() || arquivo.name,
        tipo: ext as "pdf" | "jpg" | "jpeg" | "png",
        base64,
        dataVencimento: vencDoc.trim() === "" ? null : vencDoc,
      });
    } catch (e: any) {
      setAviso({ tipo: "erro", texto: e?.message ?? "Falha ao anexar." });
    } finally {
      setEnviando(false);
    }
  }

  async function baixar(documentoId: number, nome: string) {
    try {
      const r = await utils.colaboradores.baixarDocumento.fetch({ profissionalId, documentoId });
      baixarArquivo(r.base64, r.mime, r.nomeArquivo || nome);
    } catch (e: any) {
      setAviso({ tipo: "erro", texto: e?.message ?? "Falha ao baixar." });
    }
  }

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando a ficha…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {q.error.message}
      </div>
    );
  }
  if (!prof) return null;

  const dia = aniversario(soData(ficha.data_nascimento) || prof.dataNascimento);

  return (
    <div className="space-y-4">
      {/* ── Cabeçalho ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-slate-900">{prof.nome}</h2>
            <Badge variant={prof.ativo ? "ativo" : "inativo"}>{prof.ativo ? "Ativo" : "Inativo"}</Badge>
            {modalidadeAtual && (
              <Badge variant="outline">{ROTULO_MODALIDADE[modalidadeAtual] ?? modalidadeAtual}</Badge>
            )}
            {prof.setor && <Badge variant="default">{ROTULO_SETOR[prof.setor]}</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {comoTexto(ficha.cargo) || rotuloTipo(prof.tipo)}
            {prof.userEmail ? ` · ${prof.userEmail}` : ""}
            {dia ? ` · aniversário em ${dia}` : ""}
          </p>
        </div>
        {/* Atalhos do RH. Somem no modo leitura (a ficha embutida em Meu
            extrato): /pagamentos e /juridico exigem financeiro, e link que
            leva a porta fechada é pior do que link nenhum. */}
        {!somenteLeitura && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/pagamentos">Pagamentos</Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/juridico">Jurídico</Link>
            </Button>
          </div>
        )}
      </div>

      {/* ── Setor: o único campo que grava no escalas ───────────────── */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
        <span className="text-sm font-medium text-slate-700">Setor</span>
        {(["clinica", "agro", "bt"] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={prof.setor === s ? "default" : "outline"}
            disabled={!podeEditar || salvarSetor.isPending}
            onClick={() => salvarSetor.mutate({ profissionalId, setor: prof.setor === s ? null : s })}
          >
            {ROTULO_SETOR[s]}
          </Button>
        ))}
        <span className="text-xs text-slate-500">
          {prof.setor === null
            ? "Sem setor definido — clique para escolher."
            : "Clique de novo no setor atual para limpar."}
          {dados?.setorInferido ? ` A clínica infere "${ROTULO_SETOR[dados.setorInferido] ?? dados.setorInferido}" pelo CNPJ contratante.` : ""}
        </span>
      </div>

      {q.data?.erro && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {q.data.erro}{" "}
            {prof.vcmProfissionalId === null && (
              <Link className="font-medium underline" to="/admin/vinculos">
                Resolver em Vínculos
              </Link>
            )}
          </span>
        </div>
      )}

      {aviso && (
        <div
          className={
            aviso.tipo === "ok"
              ? "flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              : "flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          }
        >
          {aviso.tipo === "ok" ? <Check className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{aviso.texto}</span>
        </div>
      )}

      {/* ── Faixa do checklist ──────────────────────────────────────── */}
      {checklist && (
        <div
          className={
            checklist.pronto
              ? "rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3"
              : "rounded-md border border-red-200 bg-red-50 px-4 py-3"
          }
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className={`flex items-center gap-2 text-sm font-semibold ${checklist.pronto ? "text-emerald-800" : "text-red-800"}`}>
              {checklist.pronto ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {checklist.resumo}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <span>Simular modalidade:</span>
              {(["clt", "pf_autonomo", "pj", "socio", "estagio", "freela"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setSimulada(simulada === m ? null : m)}
                  className={`rounded-full px-2 py-0.5 ${simulada === m ? "bg-slate-800 text-white" : "border border-slate-300 text-slate-700"}`}
                >
                  {ROTULO_MODALIDADE[m]}
                </button>
              ))}
            </div>
          </div>

          {checklist.bloqueios.length > 0 && (
            <ul className="mt-2 space-y-1">
              {checklist.bloqueios.map((b) => (
                <li key={`b-${b.campo}-${b.rotulo}`} className="flex items-start gap-2 text-sm text-red-800">
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    <button type="button" className="font-medium underline" onClick={() => focarCampo(b.campo)}>
                      {b.rotulo}
                    </button>{" "}
                    — {b.motivo}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {checklist.pendencias.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {checklist.pendencias.map((p) => (
                <button
                  key={`p-${p.campo}`}
                  type="button"
                  onClick={() => focarCampo(p.campo)}
                  title={p.motivo}
                  className="rounded-full border border-red-300 bg-white px-2.5 py-0.5 text-xs font-medium text-red-800"
                >
                  {p.rotulo}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {podeEditar && pendenciasEmpresa.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-center gap-2 font-semibold">
            <Building2 className="h-4 w-4 shrink-0" />
            Pendências da empresa contratante
            {empresaContratante ? ` — ${empresaContratante.apelido || empresaContratante.razaoSocial}` : ""}
          </div>
          <p className="mt-1 text-xs text-amber-800">
            Faltam no cadastro da empresa, não no da pessoa: não entram na contagem acima e saem como
            faltantes no contrato gerado. Corrige-se no cadastro da empresa contratante, no ERP.
          </p>
          <ul className="mt-2 space-y-1">
            {pendenciasEmpresa.map((p) => (
              <li key={`e-${p.campo}-${p.rotulo}`}>
                <span className="font-medium">{p.rotulo}</span> — {p.motivo}
              </li>
            ))}
          </ul>
        </div>
      )}

      {checklist && checklist.alertas.length > 0 && (
        <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {checklist.alertas.map((a) => (
            <div key={`a-${a.campo}-${a.rotulo}`} className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>{a.rotulo}:</strong> {a.motivo}
              </span>
            </div>
          ))}
        </div>
      )}

      {dados?.temTurnoRecorrente && modalidadeEmUso === "pf_autonomo" && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Esta pessoa tem turno recorrente na escala. PF autônoma para quem cumpre escala fixa é
            exatamente o modelo discutido na ação trabalhista em curso.
          </span>
        </div>
      )}

      {/* ── Abas ────────────────────────────────────────────────────── */}
      <Tabs defaultValue="cadastro">
        <TabsList>
          <TabsTrigger value="cadastro">Cadastro</TabsTrigger>
          <TabsTrigger value="documentos">Documentos ({dados?.documentos.length ?? 0})</TabsTrigger>
          <TabsTrigger value="contratos">Contratos ({dados?.contratos.length ?? 0})</TabsTrigger>
        </TabsList>

        {/* Cadastro ------------------------------------------------- */}
        <TabsContent value="cadastro" className="space-y-4">
          {!dados ? (
            <p className="py-6 text-sm text-slate-500">
              A ficha fiscal mora no sistema da clínica e não pôde ser carregada.
            </p>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Modalidade e empresa contratante</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="campo-modalidade">Modalidade</Label>
                    <select
                      id="campo-modalidade"
                      disabled={!podeEditar}
                      className="mt-1 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-60"
                      value={edicao.modalidade ?? comoTexto(ficha.modalidade)}
                      onChange={(e) => mudar("modalidade", e.target.value)}
                    >
                      <option value="">Sem modalidade definida</option>
                      {dados.modalidades.map((m) => (
                        <option key={m.valor} value={m.valor}>
                          {m.rotulo}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      {dados.modalidades.find((m) => m.valor === modalidadeEmUso)?.descricao ??
                        "Sem modalidade o sistema não sabe quais dados exigir."}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="campo-empresa_cnpj">Empresa contratante</Label>
                    <select
                      id="campo-empresa_cnpj"
                      disabled={!podeEditar}
                      className="mt-1 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-60"
                      value={edicao.empresa_cnpj ?? comoTexto(ficha.empresa_cnpj)}
                      onChange={(e) => mudar("empresa_cnpj", e.target.value)}
                    >
                      <option value="">Sem CNPJ contratante</option>
                      {dados.empresasContratantes.map((e) => (
                        <option key={e.cnpj} value={e.cnpj}>
                          {e.apelido} — {e.nome}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      O sistema grava CNPJ, nunca marca: "Vet Center Miguel" é marca, não parte
                      contratante.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {GRUPOS.filter((g) => !g.so || (modalidadeEmUso && g.so.includes(modalidadeEmUso))).map((grupo) => (
                <Card key={grupo.titulo}>
                  <CardHeader>
                    <CardTitle className="text-base">{grupo.titulo}</CardTitle>
                    {grupo.nota && <p className="text-xs text-slate-500">{grupo.nota}</p>}
                  </CardHeader>
                  <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {grupo.campos.map((c) => (
                      <div key={c.campo}>
                        <Label htmlFor={`campo-${c.campo}`}>{c.rotulo}</Label>
                        <Input
                          id={`campo-${c.campo}`}
                          className="mt-1"
                          type={c.tipo === "data" ? "date" : "text"}
                          disabled={!podeEditar}
                          value={valor(c.campo)}
                          onChange={(e) => mudar(c.campo, e.target.value)}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}

              {modalidadeEmUso === "socio" && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sociedade</CardTitle>
                    <p className="text-xs text-slate-500">
                      Sócio minoritário gera minuta e vai a registro na JUCESC — não é contrato de
                      prestação de serviço.
                    </p>
                  </CardHeader>
                  <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {CAMPOS_SOCIETARIO.map((c) => (
                      <div key={c.campo}>
                        <Label htmlFor={`campo-${c.campo}`}>{c.rotulo}</Label>
                        <Input
                          id={`campo-${c.campo}`}
                          className="mt-1"
                          type={c.tipo === "data" ? "date" : c.tipo === "texto" ? "text" : "number"}
                          step={c.tipo === "reais" ? "0.01" : undefined}
                          disabled={!podeEditar}
                          value={valorSoc(c.campo, c.tipo)}
                          onChange={(e) => setEdicaoSoc((a) => ({ ...a, [c.campo]: e.target.value }))}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {podeEditar && (
                <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white/95 py-3">
                  {temMudanca && (
                    <span className="text-xs text-slate-500">
                      {Object.keys(camposAlterados).length} campo(s) alterado(s) — nada foi gravado
                      ainda.
                    </span>
                  )}
                  <Button
                    variant="outline"
                    disabled={!temMudanca || salvarFiscal.isPending}
                    onClick={() => {
                      setEdicao({});
                      setEdicaoSoc({});
                    }}
                  >
                    Descartar
                  </Button>
                  <Button
                    disabled={!temMudanca || salvarFiscal.isPending}
                    onClick={() => salvarFiscal.mutate({ profissionalId, dados: camposAlterados as any })}
                  >
                    {salvarFiscal.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="mr-2 h-4 w-4" />
                    )}
                    Gravar na ficha da clínica
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Documentos ----------------------------------------------- */}
        <TabsContent value="documentos" className="space-y-4">
          {podeEditar && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Anexar documento</CardTitle>
                <p className="text-xs text-slate-500">
                  PDF, JPG ou PNG, até 10 MB. O arquivo é guardado na ficha da clínica.
                </p>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="lg:col-span-2">
                  <Label htmlFor="doc-arquivo">Arquivo</Label>
                  <input
                    id="doc-arquivo"
                    ref={arquivoRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:border-slate-300 file:bg-white file:px-3 file:py-1.5 file:text-sm"
                  />
                </div>
                <div>
                  <Label htmlFor="doc-nome">Nome do documento</Label>
                  <Input
                    id="doc-nome"
                    className="mt-1"
                    placeholder="RG, contrato social, CRMV…"
                    value={nomeDoc}
                    onChange={(e) => setNomeDoc(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="doc-venc">Vence em (opcional)</Label>
                  <Input id="doc-venc" className="mt-1" type="date" value={vencDoc} onChange={(e) => setVencDoc(e.target.value)} />
                </div>
                <div className="sm:col-span-2 lg:col-span-4">
                  <Button onClick={anexar} disabled={enviando || enviarDoc.isPending}>
                    {enviando || enviarDoc.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Anexar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {(dados?.documentos.length ?? 0) === 0 ? (
            <p className="py-6 text-sm text-slate-500">Nenhum documento anexado.</p>
          ) : (
            <div className="divide-y divide-slate-200 rounded-md border border-slate-200">
              {dados!.documentos.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{d.tipo || d.nomeArquivo}</p>
                      <p className="text-xs text-slate-500">
                        {d.criadoEm ? `Enviado em ${new Date(d.criadoEm).toLocaleDateString("pt-BR")}` : ""}
                        {d.dataVencimento ? ` · vence em ${dataBr(d.dataVencimento)}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => baixar(d.id, d.nomeArquivo ?? "documento")}>
                      <Download className="mr-2 h-4 w-4" />
                      Baixar
                    </Button>
                    {podeEditar && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={removerDoc.isPending}
                        onClick={() => {
                          if (window.confirm(`Remover "${d.tipo || d.nomeArquivo}" da ficha? O arquivo some do servidor.`)) {
                            removerDoc.mutate({ profissionalId, documentoId: d.id });
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Contratos ------------------------------------------------ */}
        <TabsContent value="contratos" className="space-y-3">
          <p className="text-xs text-slate-500">
            Gerar minuta, mandar para o advogado e marcar assinatura acontecem na tela Jurídico.
            Aqui é só o retrato do que existe.
          </p>
          {(dados?.contratos.length ?? 0) === 0 ? (
            <p className="py-6 text-sm text-slate-500">Nenhum contrato gerado para esta pessoa.</p>
          ) : (
            <div className="divide-y divide-slate-200 rounded-md border border-slate-200">
              {dados!.contratos.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {ROTULO_MODALIDADE[c.modalidade] ?? c.modalidade} · versão {c.templateVersao}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.geradoEm ? `Gerado em ${new Date(c.geradoEm).toLocaleDateString("pt-BR")}` : ""}
                        {c.assinadoEm ? ` · assinado em ${new Date(c.assinadoEm).toLocaleDateString("pt-BR")}` : ""}
                      </p>
                    </div>
                  </div>
                  <Badge variant={c.status === "assinado" ? "ativo" : "outline"}>{c.status}</Badge>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-500">Admissão: {dataBr(comoTexto(ficha.data_admissao))}</span>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default FichaColaborador;
